// Copyright (c) Docugami, Inc. All rights reserved.

import { Box } from "store/shared_types";

export class CanvasHelperStatics {
    // Draw a selection rectangle
    public static drawSelection(
        ctx: CanvasRenderingContext2D,
        bbox: Box,
        color: string,
        mulitply: boolean,
        virtualized: boolean,
        virtualizedScroller: HTMLDivElement | undefined): void {

        let bboxCopy = { ...bbox };
        if (virtualized) {
            if (!virtualizedScroller) {
                return;
            }

            const virtualWindowTop = virtualizedScroller.scrollTop;
            const virtualWindowBottom = virtualWindowTop + virtualizedScroller.offsetHeight;

            const bboxVirtualTop = Math.max(bboxCopy.top, virtualWindowTop);
            const bboxVirtualBottom = Math.min(bboxCopy.bottom, virtualWindowBottom);

            if (bboxVirtualTop < bboxVirtualBottom) {
                bboxCopy.top = bboxVirtualTop - virtualWindowTop;
                bboxCopy.bottom = bboxVirtualBottom - virtualWindowTop;
            } else {
                return;
            }
        }

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = mulitply ? 'multiply' : 'source-over';
        ctx.fillStyle = color;
        ctx.fillRect(bboxCopy.left, bboxCopy.top, bboxCopy.right - bboxCopy.left, bboxCopy.bottom - bboxCopy.top);
    }

    public static drawBox(
        ctx: CanvasRenderingContext2D,
        bbox: Box,
        color: string,
        mulitply: boolean,
        virtualized: boolean,
        strokeWeight: number,
        virtualizedScroller: HTMLDivElement | undefined) {
        const bboxCopy = { ...bbox };
        if (virtualized) {
            if (!virtualizedScroller) {
                return;
            }

            const virtualWindowTop = virtualizedScroller.scrollTop;
            const virtualWindowBottom = virtualWindowTop + virtualizedScroller.offsetHeight;

            const bboxVirtualTop = Math.max(bboxCopy.top, virtualWindowTop);
            const bboxVirtualBottom = Math.min(bboxCopy.bottom, virtualWindowBottom);

            if (bboxVirtualTop < bboxVirtualBottom) {
                bboxCopy.top = bboxVirtualTop - virtualWindowTop;
                bboxCopy.bottom = bboxVirtualBottom - virtualWindowTop;
            }
            else {
                return;
            }
        }

        ctx.lineWidth = strokeWeight;
        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = mulitply ? 'multiply' : 'source-over';
        ctx.strokeStyle = color;
        ctx.strokeRect(bboxCopy.left, bboxCopy.top, bboxCopy.right - bboxCopy.left, bboxCopy.bottom - bboxCopy.top);
    }

    public static drawCircle(
        ctx: CanvasRenderingContext2D,
        centerX: number,
        centerY: number,
        radius: number,
        color: string,
        mulitply: boolean,
        virtualized: boolean,
        virtualizedScroller: HTMLDivElement | undefined): void {

        if (virtualized) {
            if (!virtualizedScroller) {
                return;
            }

            const top = centerY - radius;
            const bot = centerY + radius;

            const virtualWindowTop = virtualizedScroller.scrollTop;
            const virtualWindowBottom = virtualWindowTop + virtualizedScroller.offsetHeight;

            const virtualTop = Math.max(top, virtualWindowTop);
            const virtualBot = Math.min(bot, virtualWindowBottom);

            if (virtualTop < virtualBot) {
                centerY = virtualTop - virtualWindowTop + 5;
            } else {
                return;
            }
        }

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = mulitply ? 'multiply' : 'source-over';
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
    }

    public static drawPath(
        ctx: CanvasRenderingContext2D,
        left: number,
        bottom: number,
        path: string,
        color: string,
        virtualizedScroller: HTMLDivElement | undefined) {
        if (!virtualizedScroller) {
            return;
        }

        bottom -= virtualizedScroller.scrollTop;

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = color;
        ctx.translate(left, bottom);
        const p = new Path2D(path);
        ctx.fill(p);
        ctx.translate(-1 * left, -1 * bottom);
    }

    public static drawPathNew(
        ctx: CanvasRenderingContext2D,
        left: number,
        bottom: number,
        path: string,
        color: string,
        scale?: number) {
        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = 'source-over';
        ctx.fillStyle = color;
        ctx.translate(left, bottom);
        const p = new Path2D(path);
        if (!!scale) {
            ctx.scale(scale, scale);
        }

        ctx.fill(p);
    }

    public static fillRectangle(
        ctx: CanvasRenderingContext2D,
        rect: Box,
        color: string,
        multiply: boolean): void {

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = multiply ? 'multiply' : 'source-over';
        ctx.fillStyle = color;
        ctx.fillRect(rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top);
    }

    public static strokeRectangle(
        ctx: CanvasRenderingContext2D,
        rect: Box,
        strokeColor: string,
        width: number,
        multiply: boolean,
        fillColor?: string): void {

        ctx.globalAlpha = 1.0;
        ctx.globalCompositeOperation = multiply ? 'multiply' : 'source-over';
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = width;
        if (!!fillColor) {
            ctx.fillStyle = fillColor;
            ctx.fillRect(rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top);
        }

        ctx.strokeRect(rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top);
    }
}
